/* ================================================================
   图纸导出模态框 · 复刻 local-clone 的图纸工作区
   - 左：A4 图纸预览（canvas 实时渲染当前选中页）
   - 右上：5 页导航
   - 右下：标注 / 编辑 / 保存 三个 tab
   - 保存 tab 的 PDF 按钮 → 选页对话框 → 调 exportPagesToPdf
   ================================================================ */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, FileText, ImageIcon, FileType,
  Edit3, Layers, Droplet, EyeOff,
  Tag, Undo2, ZoomIn, ZoomOut, ChevronLeft,
} from 'lucide-react';
import { Button } from '@/components/ui';
import {
  PAGE_PARTS,
  exportPagesToPdf,
  exportBomTablePdf,
  renderPageToCanvas,
  type DrawingContext,
  type PageNo,
} from './renderDrawing';

type TabId = 'annotate' | 'edit' | 'save';

interface DrawingExportModalProps {
  open: boolean;
  onClose: () => void;
  context: DrawingContext;
}

export default function DrawingExportModal({ open, onClose, context }: DrawingExportModalProps) {
  const [activePageNo, setActivePageNo] = useState<PageNo>(1);
  const [activeTab, setActiveTab] = useState<TabId>('annotate');
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfPageStatus, setPdfPageStatus] = useState<boolean[]>([true, true, true, true, true]);
  const [exporting, setExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 草稿状态：modal 内对图纸字段的编辑只在本次预览/导出生效，不污染外部
  const [draft, setDraft] = useState<DrawingContext>(context);
  useEffect(() => { setDraft(context); }, [context]);

  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // ESC 关闭 + 锁滚
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // 渲染当前页到预览 canvas
  useEffect(() => {
    if (!open) return;
    const c = previewCanvasRef.current;
    if (!c) return;
    let cancelled = false;
    void (async () => {
      try {
        await renderPageToCanvas(c, activePageNo, draft, 5);
        if (cancelled) return;
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { cancelled = true; };
  }, [open, activePageNo, draft]);

  const pagesSummary = useMemo(() => {
    const dim = `${draft.outerSize.length}×${draft.outerSize.width}×${draft.outerSize.height}mm`;
    return PAGE_PARTS.map(p => ({
      pageNo: p.pageNo as PageNo,
      label: p.label,
      meta: p.pageNo === 1
        ? `${draft.companyName} · 3D · 总览`
        : `${draft.companyName} · ${p.label} · ${dim}`,
    }));
  }, [draft]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ironwood/50 p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[1480px] flex-col overflow-hidden rounded-md bg-white shadow-pop"
        style={{ height: 'min(880px, calc(100vh - 32px))' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部条 */}
        <div className="flex items-center justify-between border-b border-birch-200 px-5 py-3">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-oak-500">DRAWING · 图纸预览</div>
            <h2 className="mt-0.5 text-md font-semibold text-ironwood">
              {context.orderNo}
              <span className="ml-3 text-sm font-normal text-walnut-700">
                {context.outerSize.length} × {context.outerSize.width} × {context.outerSize.height} mm
              </span>
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-sm p-1.5 text-oak-500 hover:bg-pine-100 hover:text-ironwood"
            aria-label="关闭"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* 主体 */}
        <div className="flex flex-1 overflow-hidden">
          {/* 左：A4 预览 */}
          <div className="flex flex-1 items-center justify-center overflow-auto bg-pine-50 p-6">
            <div className="rounded-md bg-white shadow-card" style={{ padding: 12 }}>
              <canvas
                ref={previewCanvasRef}
                className="block"
                style={{ width: 'min(100%, 1100px)', height: 'auto', aspectRatio: '1684 / 1191' }}
              />
            </div>
          </div>

          {/* 右：抽屉 */}
          <aside className="flex w-[360px] flex-col border-l border-birch-200 bg-white">
            {/* 页面导航 */}
            <div className="border-b border-birch-200 p-3">
              <div className="mb-2 text-xs uppercase tracking-[0.12em] text-oak-500">图纸工作区</div>
              <div className="text-base font-semibold text-ironwood">
                第{numberToCN(activePageNo)}页
              </div>
              <div className="mt-0.5 text-xs text-walnut-700">
                {pagesSummary.find(p => p.pageNo === activePageNo)?.meta}
              </div>
            </div>

            <div className="space-y-1.5 p-3">
              {pagesSummary.map((p) => {
                const active = p.pageNo === activePageNo;
                return (
                  <button
                    key={p.pageNo}
                    onClick={() => setActivePageNo(p.pageNo)}
                    className={`flex w-full items-center gap-3 rounded-sm border px-3 py-2 text-left transition-colors ${
                      active
                        ? 'border-wood-500 bg-wood-100 text-walnut-900'
                        : 'border-birch-200 bg-white text-walnut-700 hover:bg-pine-50'
                    }`}
                  >
                    <div
                      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-sm font-mono text-md font-semibold ${
                        active ? 'bg-wood-500 text-white' : 'bg-pine-100 text-walnut-700'
                      }`}
                    >
                      {p.pageNo}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">第{numberToCN(p.pageNo)}页</div>
                      <div className="truncate text-xs text-oak-500">{p.meta}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* 三 tab */}
            <div className="border-t border-birch-200">
              <div className="flex border-b border-birch-200">
                {(['annotate', 'edit', 'save'] as const).map((id) => {
                  const labels = { annotate: '标注', edit: '编辑', save: '保存' };
                  const active = activeTab === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setActiveTab(id)}
                      className={`flex-1 px-3 py-2.5 text-sm transition-colors ${
                        active
                          ? 'border-b-2 border-wood-500 bg-wood-100 font-semibold text-walnut-900'
                          : 'border-b-2 border-transparent text-walnut-700 hover:bg-pine-50'
                      }`}
                    >
                      {labels[id]}
                    </button>
                  );
                })}
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {activeTab === 'annotate' && (
                  <AnnotateTab />
                )}
                {activeTab === 'edit' && (
                  <EditTab draft={draft} setDraft={setDraft} />
                )}
                {activeTab === 'save' && (
                  <SaveTab
                    activePageNo={activePageNo}
                    context={draft}
                    exporting={exporting}
                    onOpenPdfDialog={() => setPdfDialogOpen(true)}
                    onError={setErrorMessage}
                  />
                )}

                {errorMessage && (
                  <div className="mt-3 rounded-sm border border-danger bg-danger-soft px-3 py-2 text-xs text-danger">
                    {errorMessage}
                  </div>
                )}
              </div>
            </div>

            {/* 底部说明 */}
            <div className="border-t border-birch-200 bg-pine-100 px-4 py-2.5">
              <div className="text-xs text-oak-500">
                {activeTab === 'annotate' && '在图纸上标注尺寸、文字（v1 待补充）'}
                {activeTab === 'edit' && '修改说明、明细、材料、水印（v1 待补充）'}
                {activeTab === 'save' && `第${numberToCN(activePageNo)}页已准备就绪，可直接导出图片或 PDF`}
              </div>
            </div>
          </aside>
        </div>

        {/* 选页对话框 */}
        {pdfDialogOpen && (
          <SelectPdfPagesDialog
            pdfPageStatus={pdfPageStatus}
            onChange={setPdfPageStatus}
            onCancel={() => setPdfDialogOpen(false)}
            onConfirm={async () => {
              setPdfDialogOpen(false);
              const selected: PageNo[] = pdfPageStatus
                .map((checked, idx) => (checked ? ((idx + 1) as PageNo) : null))
                .filter((p): p is PageNo => p !== null);
              if (selected.length === 0) {
                setErrorMessage('请至少选择一页');
                return;
              }
              setErrorMessage(null);
              setExporting(true);
              try {
                await exportPagesToPdf(selected, draft);
              } catch (err) {
                setErrorMessage((err as Error).message || 'PDF 导出失败');
              } finally {
                setExporting(false);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

// --------------- Tab 内容 ---------------

function AnnotateTab() {
  const buttons = [
    { icon: Tag, label: '自定义模板', tone: 'wood' as const },
    { icon: Undo2, label: '撤销', tone: 'default' as const },
    { icon: ZoomIn, label: '放大', tone: 'default' as const },
    { icon: ZoomOut, label: '缩小', tone: 'default' as const },
  ];
  return (
    <div className="grid grid-cols-2 gap-2">
      {buttons.map(b => (
        <ToolButton key={b.label} icon={b.icon} label={b.label} tone={b.tone} disabled />
      ))}
    </div>
  );
}

type EditFormId = null | 'description' | 'detail' | 'material' | 'watermark' | 'hideDrawing';

function EditTab({
  draft,
  setDraft,
}: {
  draft: DrawingContext;
  setDraft: React.Dispatch<React.SetStateAction<DrawingContext>>;
}) {
  const [activeForm, setActiveForm] = useState<EditFormId>(null);

  if (activeForm === 'watermark') {
    return (
      <FormShell title="修改水印" onBack={() => setActiveForm(null)}>
        <FormField label="水印文字">
          <input
            type="text"
            value={draft.watermark}
            onChange={(e) => setDraft({ ...draft, watermark: e.target.value })}
            className="h-9 w-full rounded-sm border border-birch-200 bg-white px-2 text-sm text-ironwood outline-none focus:border-wood-500"
            placeholder="留空则隐藏水印"
          />
        </FormField>
        <p className="text-xs text-oak-500">提示：水印为防盗用斜向半透明文字，每页都会生效。</p>
      </FormShell>
    );
  }

  if (activeForm === 'detail') {
    return (
      <FormShell title="修改明细" onBack={() => setActiveForm(null)}>
        <FormField label="客户">
          <input
            type="text" value={draft.customerName}
            onChange={(e) => setDraft({ ...draft, customerName: e.target.value })}
            className={inputCls}
          />
        </FormField>
        <FormField label="客户ID">
          <input
            type="text" value={String(draft.customerId)}
            onChange={(e) => setDraft({ ...draft, customerId: e.target.value })}
            className={inputCls}
          />
        </FormField>
        <FormField label="制图人">
          <input
            type="text" value={draft.designerName}
            onChange={(e) => setDraft({ ...draft, designerName: e.target.value })}
            className={inputCls}
          />
        </FormField>
        <FormField label="审核人">
          <input
            type="text" value={draft.reviewerName}
            onChange={(e) => setDraft({ ...draft, reviewerName: e.target.value })}
            className={inputCls}
          />
        </FormField>
        <FormField label="图纸编号">
          <input
            type="text" value={draft.drawingNumber}
            onChange={(e) => setDraft({ ...draft, drawingNumber: e.target.value })}
            className={inputCls}
          />
        </FormField>
        <FormField label="制图日期">
          <input
            type="date" value={draft.makeDate}
            onChange={(e) => setDraft({ ...draft, makeDate: e.target.value })}
            className={inputCls}
          />
        </FormField>
        <FormField label="审核日期">
          <input
            type="date" value={draft.auditDate}
            onChange={(e) => setDraft({ ...draft, auditDate: e.target.value })}
            className={inputCls}
          />
        </FormField>
      </FormShell>
    );
  }

  if (activeForm === 'description') {
    return (
      <FormShell title="修改说明" onBack={() => setActiveForm(null)}>
        <FormField label="内装物重量 (Kg)">
          <input
            type="number"
            value={draft.cargoWeight ?? ''}
            onChange={(e) => setDraft({ ...draft, cargoWeight: e.target.value === '' ? undefined : Number(e.target.value) })}
            className={inputCls}
          />
        </FormField>
        <FormField label="木箱重量 (Kg)">
          <input
            type="number"
            value={draft.crateWeight ?? ''}
            onChange={(e) => setDraft({ ...draft, crateWeight: e.target.value === '' ? undefined : Number(e.target.value) })}
            className={inputCls}
          />
        </FormField>
        <FormField label="公司名（顶部标题）">
          <input
            type="text" value={draft.companyName}
            onChange={(e) => setDraft({ ...draft, companyName: e.target.value })}
            className={inputCls}
          />
        </FormField>
      </FormShell>
    );
  }

  // 默认：按钮网格
  const buttons: Array<{ icon: React.ComponentType<{ className?: string }>; label: string; form: EditFormId }> = [
    { icon: FileText, label: '修改说明', form: 'description' },
    { icon: Edit3, label: '修改明细', form: 'detail' },
    { icon: Layers, label: '编辑材料', form: 'material' },
    { icon: Droplet, label: '修改水印', form: 'watermark' },
    { icon: EyeOff, label: '隐藏图纸', form: 'hideDrawing' },
  ];
  return (
    <div className="grid grid-cols-2 gap-2">
      {buttons.map(b => (
        <ToolButton
          key={b.label}
          icon={b.icon}
          label={b.label}
          tone="default"
          onClick={b.form === 'material' || b.form === 'hideDrawing' ? undefined : () => setActiveForm(b.form)}
          disabled={b.form === 'material' || b.form === 'hideDrawing'}
        />
      ))}
    </div>
  );
}

const inputCls =
  'h-9 w-full rounded-sm border border-birch-200 bg-white px-2 text-sm text-ironwood outline-none focus:border-wood-500';

function FormShell({ title, onBack, children }: { title: string; onBack: () => void; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-walnut-700 hover:bg-pine-50"
      >
        <ChevronLeft className="h-3 w-3" /> 返回
      </button>
      <div className="text-sm font-semibold text-ironwood">{title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs text-oak-500">{label}</div>
      {children}
    </div>
  );
}

function SaveTab({
  activePageNo,
  context,
  exporting,
  onOpenPdfDialog,
  onError,
}: {
  activePageNo: PageNo;
  context: DrawingContext;
  exporting: boolean;
  onOpenPdfDialog: () => void;
  onError: (msg: string | null) => void;
}) {
  async function handleSaveImage() {
    try {
      onError(null);
      const c = document.createElement('canvas');
      await renderPageToCanvas(c, activePageNo, context, 5);
      const url = c.toDataURL('image/jpeg', 0.95);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${context.customerName || '客户'}_${context.orderNo || '订单'}_第${numberToCN(activePageNo)}页.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      onError((err as Error).message || '图片导出失败');
    }
  }
  async function handleSaveBom() {
    try {
      onError(null);
      await exportBomTablePdf(activePageNo, context);
    } catch (err) {
      onError((err as Error).message || '料单导出失败');
    }
  }
  return (
    <div className="grid grid-cols-3 gap-2">
      <ToolButton icon={FileText} label="本图料单" onClick={handleSaveBom} tone="default" />
      <ToolButton icon={ImageIcon} label="图片" onClick={handleSaveImage} tone="default" />
      <ToolButton
        icon={FileType}
        label={exporting ? '导出中...' : 'PDF'}
        onClick={onOpenPdfDialog}
        tone="primary"
        disabled={exporting}
      />
    </div>
  );
}

function ToolButton({
  icon: Icon,
  label,
  onClick,
  tone = 'default',
  disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick?: () => void;
  tone?: 'default' | 'primary' | 'wood';
  disabled?: boolean;
}) {
  const toneCls =
    tone === 'primary'
      ? 'border-danger bg-danger-soft text-danger hover:bg-danger/10'
      : tone === 'wood'
        ? 'border-wood-500 bg-wood-100 text-walnut-900 hover:bg-wood-100/70'
        : 'border-birch-200 bg-white text-walnut-700 hover:bg-pine-50';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center justify-center gap-1.5 rounded-sm border px-2 py-3 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${toneCls}`}
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </button>
  );
}

// --------------- 选页对话框 ---------------

function SelectPdfPagesDialog({
  pdfPageStatus,
  onChange,
  onCancel,
  onConfirm,
}: {
  pdfPageStatus: boolean[];
  onChange: (next: boolean[]) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-ironwood/40 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-md bg-white shadow-pop"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-birch-200 px-5 py-3">
          <h3 className="text-md font-semibold text-ironwood">选择导出页</h3>
        </div>
        <div className="space-y-2 px-5 py-4">
          {PAGE_PARTS.map((p, i) => {
            const checked = pdfPageStatus[i];
            return (
              <label
                key={p.pageNo}
                className="flex cursor-pointer items-center gap-3 rounded-sm border border-birch-200 px-3 py-2 hover:bg-pine-50"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = [...pdfPageStatus];
                    next[i] = e.target.checked;
                    onChange(next);
                  }}
                  className="h-4 w-4 accent-wood-500"
                />
                <span className="font-mono text-sm font-semibold text-walnut-900">{p.pageNo}</span>
                <span className="text-sm text-walnut-700">第{numberToCN(p.pageNo as PageNo)}页 · {p.label}</span>
              </label>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 border-t border-birch-200 bg-pine-100 px-5 py-3">
          <Button variant="ghost" onClick={onCancel}>取消</Button>
          <Button onClick={onConfirm}>确定导出</Button>
        </div>
      </div>
    </div>
  );
}

const CN_NUM = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
function numberToCN(n: number): string {
  return CN_NUM[n] ?? String(n);
}
